import { useState } from "react";
import { usePendingRegistrations, useApproveRegistration, useRejectRegistration, type RegistrationSummary } from "../api/registrations";
import "./admin-registrations-panel.css";

export const AdminRegistrationsPanel = (): JSX.Element => {
    const [search, setSearch] = useState("");
    const [selectedRegistration, setSelectedRegistration] = useState<RegistrationSummary | null>(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [approvalNotes, setApprovalNotes] = useState("");
    const [rejectReason, setRejectReason] = useState("");

    const { data: registrations = [], isLoading, error, refetch } = usePendingRegistrations();
    const approveMutation = useApproveRegistration();
    const rejectMutation = useRejectRegistration();

    // Filter registrations by search term
    const filteredRegistrations = registrations.filter((reg) => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            reg.userName.toLowerCase().includes(searchLower) ||
            reg.email.toLowerCase().includes(searchLower) ||
            reg.requestedRole.toLowerCase().includes(searchLower)
        );
    });

    const handleApprove = (registration: RegistrationSummary) => {
        setSelectedRegistration(registration);
        setShowApproveModal(true);
        setApprovalNotes("");
    };

    const handleReject = (registration: RegistrationSummary) => {
        setSelectedRegistration(registration);
        setShowRejectModal(true);
        setRejectReason("");
    };

    const confirmApprove = async () => {
        if (!selectedRegistration) return;

        try {
            await approveMutation.mutateAsync({
                registrationId: selectedRegistration.id,
                approvalNotes: approvalNotes || undefined,
            });
            setShowApproveModal(false);
            setSelectedRegistration(null);
            setApprovalNotes("");
            await refetch();
        } catch (error) {
            alert("Kunde inte godkänna registreringen");
        }
    };

    const confirmReject = async () => {
        if (!selectedRegistration || !rejectReason.trim()) {
            alert("Ange en anledning för avvisning");
            return;
        }

        try {
            await rejectMutation.mutateAsync({
                registrationId: selectedRegistration.id,
                reason: rejectReason,
            });
            setShowRejectModal(false);
            setSelectedRegistration(null);
            setRejectReason("");
            await refetch();
        } catch (error) {
            alert("Kunde inte avvisa registreringen");
        }
    };

    if (error) {
        return (
            <div className="admin-registrations-panel">
                <h2>Väntande Registreringar</h2>
                <p className="error-message">Kunde inte ladda registreringar.</p>
            </div>
        );
    }

    return (
        <>
            <section className="admin-registrations-panel" aria-label="Väntande registreringar">
                <div className="admin-registrations-panel__header">
                    <h2>Väntande Registreringar</h2>
                    <span className="admin-registrations-panel__count">
                        {filteredRegistrations.length} väntande
                    </span>
                </div>

                <div className="admin-registrations-panel__search">
                    <input
                        type="text"
                        placeholder="Sök registreringar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                        aria-label="Sök registreringar"
                    />
                </div>

                {isLoading ? (
                    <p>Laddar...</p>
                ) : filteredRegistrations.length === 0 ? (
                    <div className="admin-registrations-panel__empty">
                        <p>Inga väntande registreringar.</p>
                    </div>
                ) : (
                    <div className="admin-registrations-panel__list">
                        {filteredRegistrations.map((registration) => (
                            <div key={registration.id} className="registration-card">
                                <div className="registration-card__header">
                                    <h3>{registration.userName}</h3>
                                    <span className="registration-card__date">
                                        {new Date(registration.createdAt).toLocaleDateString("sv-SE", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                </div>
                                <div className="registration-card__body">
                                    <div className="registration-card__field">
                                        <strong>E-post:</strong> {registration.email}
                                    </div>
                                    <div className="registration-card__field">
                                        <strong>Begärd roll:</strong> {registration.requestedRole}
                                    </div>
                                    <div className="registration-card__field">
                                        <strong>Team ID:</strong> {registration.requestedTeamId}
                                    </div>
                                    {registration.expiresAt && (
                                        <div className="registration-card__field">
                                            <strong>Utgår:</strong>{" "}
                                            {new Date(registration.expiresAt).toLocaleDateString("sv-SE")}
                                        </div>
                                    )}
                                </div>
                                <div className="registration-card__actions">
                                    <button
                                        className="action-button action-button--approve"
                                        onClick={() => handleApprove(registration)}
                                        disabled={approveMutation.isPending || rejectMutation.isPending}
                                    >
                                        Godkänn
                                    </button>
                                    <button
                                        className="action-button action-button--reject"
                                        onClick={() => handleReject(registration)}
                                        disabled={approveMutation.isPending || rejectMutation.isPending}
                                    >
                                        Avvisa
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Approve Modal */}
            {showApproveModal && selectedRegistration && (
                <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Godkänn Registrering</h3>
                        <p>
                            Är du säker på att du vill godkänna registreringen för <strong>{selectedRegistration.userName}</strong>?
                        </p>
                        <div className="modal-form">
                            <label htmlFor="approval-notes">Anteckningar (valfritt):</label>
                            <textarea
                                id="approval-notes"
                                value={approvalNotes}
                                onChange={(e) => setApprovalNotes(e.target.value)}
                                rows={3}
                                placeholder="Lägg till anteckningar om godkännande..."
                            />
                        </div>
                        <div className="modal-actions">
                            <button
                                className="action-button action-button--approve"
                                onClick={confirmApprove}
                                disabled={approveMutation.isPending}
                            >
                                {approveMutation.isPending ? "Godkänner..." : "Godkänn"}
                            </button>
                            <button
                                className="action-button"
                                onClick={() => {
                                    setShowApproveModal(false);
                                    setSelectedRegistration(null);
                                    setApprovalNotes("");
                                }}
                                disabled={approveMutation.isPending}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedRegistration && (
                <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Avvisa Registrering</h3>
                        <p>
                            Är du säker på att du vill avvisa registreringen för <strong>{selectedRegistration.userName}</strong>?
                        </p>
                        <div className="modal-form">
                            <label htmlFor="reject-reason">
                                Anledning <span className="required">*</span>:
                            </label>
                            <textarea
                                id="reject-reason"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={4}
                                placeholder="Ange anledning för avvisning..."
                                required
                            />
                        </div>
                        <div className="modal-actions">
                            <button
                                className="action-button action-button--reject"
                                onClick={confirmReject}
                                disabled={rejectMutation.isPending || !rejectReason.trim()}
                            >
                                {rejectMutation.isPending ? "Avvisar..." : "Avvisa"}
                            </button>
                            <button
                                className="action-button"
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setSelectedRegistration(null);
                                    setRejectReason("");
                                }}
                                disabled={rejectMutation.isPending}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};